import { useState, ReactNode, useEffect, useRef } from "react";
import { Button, Col, Form, Input, Radio, Row } from "antd";
import { maxLength, required } from "../../../../utils/form-validations";
import "./style.scss";
import { useGetData } from "../../../../Network/useGetQueryData";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const TypeSteps = ({
  onChangeStep,
  setFormData,
  formData,
}: {
  onChangeStep: (step: number) => void;
  setFormData?: (data: any) => void;
  formData: any;
}) => {
  const { t, i18n } = useTranslation();

  const { requestId } = useParams();
  const [loading, setLoading] = useState<boolean>();
  const [offset, setOffset] = useState(0);
  const [searchKey, setSearchKey] = useState<string>();
  const [customersList, setCustomerList] = useState<any>([]);
  const [isCustomerOpen, setIsCustomerOpen] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>();
  const [limit] = useState(10);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const { data: customers, isLoading } = useGetData<{
    data: { items: any[]; count: number };
  }>({
    queryKey: ["customer", { offset, limit, searchKey }],
    url: "/customer",
    params: { includeUser: true, offset, limit, searchKey, withDeleted: false },
    options: {
      queryKey: ["customer", { includeUser: true, offset, limit, searchKey }],
      enabled: !!!requestId,
    },
  });

  const { data: categories } = useGetData<{
    data: { items: any[]; count: number };
  }>({
    queryKey: ["category"],
    url: "/category?includeParentFilter=true&offset=0&limit=50&withDeleted=false",
  });

  const [form] = Form.useForm();

  const handleAddProvider = async (values: any) => {
    setLoading(true);
    try {
      setLoading(false);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const ServiceTypeBox = ({
    title,
    id,
    icon,
    classes,
  }: {
    title: string;
    id: string;
    classes?: string;
    icon: ReactNode;
  }) => (
    <Radio value={id} className={`service-type-box ${classes ? classes : ""}`}>
      {icon}
      <p> {title} </p>
    </Radio>
  );

  useEffect(() => {
    if (customers?.data?.items && !!searchKey) {
      setCustomerList(customers.data.items);
      return;
    }

    if (customers?.data?.items)
      setCustomerList((prevCustomers: any) => [
        ...prevCustomers,
        ...customers.data.items,
      ]);
  }, [customers]);

  const CustomerCardInfo = ({
    customerDetails,
    user,
    code,
    id,
    hasMembership,
  }: {
    customerDetails: any;
    user: any;
    code: string;
    id: number;
    hasMembership: boolean;
  }) => {
    const details = customerDetails
      ? typeof customerDetails === "string"
        ? JSON.parse(customerDetails)
        : customerDetails
      : user;

    const { firstName, phoneNumber, email, countryCode } = details ?? {};
    if (!firstName && !phoneNumber && !email) return null;

    return (
      <div
        onClick={() => {
          setSelectedCustomer({ id, value: firstName || email || phoneNumber });
          form.setFieldValue("client", firstName || email || phoneNumber);
          setIsCustomerOpen(false);
        }}
        className="customer-card-info"
        style={{
          pointerEvents: hasMembership ? "unset" : "none",
          opacity: hasMembership ? "1" : "0.5",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <p>
          {firstName ? (
            <p style={{ whiteSpace: "wrap" }}>
              {firstName} {code && `(${code})`}
            </p>
          ) : phoneNumber ? (
            <p>
              {countryCode} {phoneNumber} {code && `(${code})`}
            </p>
          ) : code ? (
            `(${code})`
          ) : null}
          {email && <span> {email} </span>}
        </p>

        {!hasMembership && (
          <span style={{ color: "red", fontSize: "12px" }}>
            {" "}
            (No membership){" "}
          </span>
        )}
      </div>
    );
  };

  const loadMoreItems = () => {
    if (
      customers &&
      customersList.length < customers?.data?.count &&
      !isLoading
    ) {
      setOffset((prevOffset) => prevOffset + 1);
    }
  };

  useEffect(() => {
    if (!formData?.customer && requestId) return;

    const { id, contactDetails, user } = formData?.customer || {};
    const details = !!contactDetails
      ? typeof contactDetails === "string"
        ? JSON.parse(contactDetails)
        : contactDetails
      : user;

    const { firstName, phoneNumber, email } = details ?? {};

    setSelectedCustomer({ id, value: firstName || email || phoneNumber });
    form.setFieldValue("client", firstName || email || phoneNumber);
    setIsCustomerOpen(false);
  }, [formData]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsCustomerOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsCustomerOpen]);

  return (
    <Form form={form} layout="vertical" initialValues={formData}>
      <p className="form-section-title">{t("typesteps.selectClient")}</p>
      <Row gutter={32}>
        <Col lg={24} xl={12} style={{ position: "relative" }}>
          <Form.Item
            rules={[required, maxLength(3000)]}
            label={t("typesteps.client")}
            name="client"
          >
            <Input.TextArea
              disabled={!!requestId}
              onChange={(e) => {
                setIsCustomerOpen(true);
                setSearchKey(e.target.value);
              }}
              onFocus={() => setIsCustomerOpen(true)}
              value={selectedCustomer?.value}
              placeholder={t("typesteps.searchPlaceholder")}
            />
          </Form.Item>
          {customersList && customersList?.length && isCustomerOpen && (
            <div
              className="customer-list-wrapper"
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } =
                  e.target as any;
                if (scrollTop + clientHeight >= scrollHeight - 10)
                  loadMoreItems?.();
              }}
              ref={wrapperRef}
            >
              {customersList?.length ? (
                customersList?.map((item: any) => (
                  <div key={item?.id}>
                    <CustomerCardInfo
                      id={item.id}
                      code={item?.code}
                      customerDetails={item?.contactDetails}
                      user={item?.user}
                      hasMembership={
                        !!item?.membershipPlanId || item?.membershipPlanId === 0
                      }
                    />
                  </div>
                ))
              ) : (
                <p>{t("typesteps.noData")}</p>
              )}
            </div>
          )}
        </Col>
      </Row>
      <Row gutter={32}>
        <p className="form-section-title">{t("typesteps.chooseServiceType")}</p>
        <Col lg={24} xl={24}>
          <div className="service-request-type">
            <Form.Item rules={[required]} name="types">
              <Radio.Group className="step-types-wrapper">
                {categories?.data?.items?.map((cat) => (
                  <ServiceTypeBox
                    title={cat?.name[i18n.language || "en"]}
                    id={cat?.id}
                    key={cat?.id}
                    icon={
                      <img
                        src={cat?.imageUrl}
                        alt="logo-url"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "/assets/requests.svg";
                        }}
                      />
                    }
                  />
                ))}
              </Radio.Group>
            </Form.Item>
          </div>
        </Col>
      </Row>

      <div className="steps-action-footer">
        <Button
          loading={loading}
          type="primary"
          style={{ marginRight: "8px" }}
          onClick={async () => {
            try {
              const values = await form.validateFields();
              setFormData?.({
                ...formData,
                ...values,
                customerId: selectedCustomer?.id,
              });
              onChangeStep(1);
            } catch (error) {}
          }}
        >
          {t("buttons.next")}
        </Button>
        <Button
          onClick={async () => {
            try {
              const values = await form.validateFields();
              await handleAddProvider(values);
            } catch (error) {}
          }}
          loading={loading}
          type="link"
        >
          {t("buttons.saveAndClose")}
        </Button>
      </div>
    </Form>
  );
};
